import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import AuthNavbar from "../components/AuthNavbar";
import LanguageSelector from "../components/LanguageSelector";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../auth/AuthContext";
import { encryptData } from "../utils/crypto";
import DotPulseLoading from "../components/Loading/DotPulseLoading";
import CustomSnackbar from "../components/CustomSnackbar";
import WhatsNewSection from "../components/WhatsNewSection";

const EyeIcon = ({ show }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    {show ? (
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
    ) : (
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    )}
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const SSOIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
  </svg>
);

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemFadeIn = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 },
};

function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 968);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleGoogleLogin = () => {
    const clientId = `${process.env.REACT_APP_CLIENT_ID}`;
    const redirectUri = `${process.env.REACT_APP_CALLBACK_URL}`;
    const scope = "email profile openid";
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&access_type=offline&prompt=consent`;
    window.location.href = authUrl;
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    const lang = localStorage.getItem("i18nextLng") || "en";
    // Basic client-side validation
    if (!email || !password) {
      setSnackbar({
        open: true,
        message: "Please fill in all fields",
        severity: "error",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/sign-in`,
        { email, password },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      const { data } = response;

      if (data.status === "success" && data.data?.token) {
        // Store the access token
        localStorage.setItem("token", data.data.token);

        // Store user data if needed
        const encrypted = encryptData(data.data.user);
        localStorage.setItem("user", encrypted);

        // Call the login function from AuthContext
        login(data.data.token, data.data.user);

        // Redirect to dashboard
        navigate("/dashboard");
      } else {
        // Handle unexpected response format
        setSnackbar({
          open: true,
          message: "Login successful but invalid response format",
          severity: "error",
        });
      }
    } catch (err) {
      // Handle different error cases
      if (err.response) {
        // The request was made and the server responded with a status code
        setSnackbar({
          open: true,
          message:
            err.response.data?.message || "Login failed. Please try again.",
          severity: "error",
        });
      } else if (err.request) {
        // The request was made but no response was received
        setSnackbar({
          open: true,
          message: "No response from server. Please check your connection.",
          severity: "error",
        });
      } else {
        // Something happened in setting up the request
        setSnackbar({
          open: true,
          message: "An error occurred. Please try again.",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Navbar Component */}
      <AuthNavbar />

      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15px, -15px); }
        }
      `}</style>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DotPulseLoading size={14} color="#8b7fa8" />
          </div>
        )}

        {/* Abstract Bubble Background - Left Side */}
        <div
          style={{
            position: "absolute",
            left: "-25%",
            top: "40%",
            transform: "translateY(-50%)",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          {/* Main Large Bubble */}
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(139, 127, 168, 0.1))",
            }}
          >
            <defs>
              <linearGradient
                id="gradient1"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#f5f1fb", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#e8dff5", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#dac9eb", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            {/* Wavy organic blob shape */}
            <path
              d="M 150,100 
               C 150,50 200,20 280,50
               C 360,80 400,120 450,200
               C 500,280 520,360 480,450
               C 440,540 400,600 350,680
               C 300,760 250,820 180,860
               C 110,900 50,880 30,800
               C 10,720 -20,640 20,540
               C 60,440 100,360 120,260
               C 140,160 150,150 150,100 Z"
              fill="url(#gradient1)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>

          {/* Smaller floating bubbles */}
          <div
            style={{
              position: "absolute",
              top: "15%",
              right: "10%",
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(218, 201, 235, 0.4), rgba(139, 127, 168, 0.2))",
              animation: "floatSlow 6s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "25%",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(232, 223, 245, 0.5), rgba(218, 201, 235, 0.3))",
              animation: "floatSlow 7s ease-in-out infinite reverse",
            }}
          />

          <div
            style={{
              position: "absolute",
              top: "45%",
              right: "5%",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(245, 241, 251, 0.6), rgba(232, 223, 245, 0.4))",
              animation: "float 5s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "10%",
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(218, 201, 235, 0.5), rgba(139, 127, 168, 0.3))",
              animation: "floatSlow 6s ease-in-out infinite reverse",
            }}
          />
        </div>

        {/* Main Large Bubble - Right Bottom */}
        <div
          style={{
            position: "absolute",
            right: "-25%",
            bottom: "10%",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(139, 127, 168, 0.1))",
              opacity: 0.2,
            }}
          >
            <defs>
              <linearGradient
                id="gradient2"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#f5f1fb", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#e8dff5", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#dac9eb", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            {/* Wavy organic blob shape - same as left */}
            <path
              d="M 150,100 
               C 150,50 200,20 280,50
               C 360,80 900,120 450,200
               C 500,280 520,360 480,450
               C 440,540 400,600 350,680
               C 300,760 250,820 180,860
               C 110,900 50,880 30,800
               C 10,720 -20,640 20,540
               C 60,440 100,360 120,260
               C 140,160 150,150 150,100 Z"
              fill="url(#gradient2)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>
        </div>

        {/* Left Sidebar - What's New (Hidden on mobile) */}
        <WhatsNewSection isMobile={isMobile} />

        {/* Right Side - Login Form */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "32px 24px" : "48px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Login Form Container */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            style={{
              width: "100%",
              maxWidth: "480px",
              marginTop: "0",
            }}
          >
            <motion.div
              variants={itemFadeIn}
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <LanguageSelector />
            </motion.div>

            <motion.h1
              variants={itemFadeIn}
              style={{
                fontSize: isMobile ? "32px" : "40px",
                fontWeight: "bold",
                color: "#1a202c",
                marginBottom: "12px",
                textAlign: "center",
              }}
            >
              {t("login.title")}
            </motion.h1>

            <motion.p
              variants={itemFadeIn}
              style={{
                fontSize: "16px",
                color: "#718096",
                textAlign: "center",
                marginBottom: "40px",
              }}
            >
              {t("login.subtitle")}
            </motion.p>

            <div onSubmit={handleSubmit}>
              {/* Email Input */}
              <motion.div variants={itemFadeIn} style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("register.emailLabel")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="user@mail.com"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    fontSize: "15px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    transition: "all 0.3s ease",
                    outline: "none",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#8b7fa8";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(139, 127, 168, 0.15)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </motion.div>

              {/* Password Input */}
              <motion.div variants={itemFadeIn} style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("register.passwordLabel")}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••"
                    style={{
                      width: "100%",
                      padding: "14px 48px 14px 16px",
                      fontSize: "15px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      outline: "none",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#8b7fa8";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(139, 127, 168, 0.15)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#d1d5db";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#718096",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <EyeIcon show={showPassword} />
                  </button>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.button
                variants={itemFadeIn}
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSubmit}
                style={{
                  width: "100%",
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "white",
                  backgroundColor: "#8b7fa8",
                  border: "none",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  marginBottom: "24px",
                  fontFamily: "inherit",
                  transition: "all 0.3s ease",
                }}
              >
                {loading
                  ? t("register.createAccountButton")
                  : t("login.signInButton")}
              </motion.button>

              {/* Links */}
              <motion.div
                variants={itemFadeIn}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  marginBottom: "32px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <a
                  href="/ForgotPassword"
                  style={{
                    color: "#8b7fa8",
                    textDecoration: "none",
                    fontWeight: "500",
                  }}
                >
                  {t("login.forgotPassword")}
                </a>
              </motion.div>

              {/* Divider */}
              <motion.div
                variants={itemFadeIn}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "24px",
                  gap: "16px",
                }}
              >
                <div
                  style={{ flex: 1, height: "1px", backgroundColor: "#d1d5db" }}
                />
                <span style={{ color: "#718096", fontSize: "14px" }}>
                  {t("login.socialDivider")}
                </span>
                <div
                  style={{ flex: 1, height: "1px", backgroundColor: "#d1d5db" }}
                />
              </motion.div>

              {/* Social Login */}
              <motion.div
                variants={itemFadeIn}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                <motion.button
                  type="button"
                  onClick={handleGoogleLogin}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: "14px",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#4a5568",
                    backgroundColor: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    fontFamily: "inherit",
                  }}
                >
                  <GoogleIcon /> Google
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => navigate("/login/sso")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: "14px",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#4a5568",
                    backgroundColor: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    fontFamily: "inherit",
                  }}
                >
                  <SSOIcon /> SSO
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Snackbar */}
        <CustomSnackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </div>
    </>
  );
}

export default Login;
